<template>
  <hcc-modal
    :title="$t('users.create')"
    class="user"
    :width="600"
    name="Usuario"
    @confirm="userAction"
    @closed="clear"
    @cancel="clear"
    @opened="focusRefs"
    :confirmDisableButton="!validForm"
  >
    <div class="user__property">
      <hcc-input
        :label="$t('common.name')"
        :placeholder="$t('common.name')"
        v-model="name"
        @blur="$v.name.$touch()"
        :error="$v.name.$error"
        ref="hccInput"
      />
      <p v-if="$v.name.$error" class="user__error">
        {{ $t("users.errors.name") }}
      </p>
      <span />
      <hcc-input
        :label="$t('common.email')"
        :placeholder="$t('common.email')"
        v-model="email"
        @blur="$v.email.$touch()"
        :error="$v.email.$error"
      />
      <template v-if="$v.email.$error">
        <p v-if="!$v.email.email" class="user__error">
          {{ $t("users.errors.email") }}
        </p>
        <p v-if="!$v.email.required" class="user__error">
          {{ $t("users.errors.email2") }}
        </p>
      </template>
      <span />
      <hcc-input
        type="password"
        :placeholder="$t('users.password')"
        v-model.trim="password"
        @blur="$v.password.$touch()"
        :error="$v.password.$error && !edit"
        :label="$t('users.password')"
      />
      <template v-if="$v.password.$error">
        <p v-if="!$v.password.minLength" class="user__error">
          {{ $t("users.errors.password-min") }}
        </p>
        <p v-if="!$v.password.required && !edit" class="user__error">
          {{ $t("users.errors.password") }}
        </p>
        <p v-if="!$v.password.valid" class="user__error">
          {{ $t("users.errors.valid-password") }}
        </p>
      </template>
      <span />
      <hcc-input
        type="password"
        :placeholder="$t('users.password-confirm')"
        v-model="confirm"
        @blur="$v.confirm.$touch()"
        :error="$v.confirm.$error"
        :label="$t('users.password-confirm')"
        @keyup.enter="submit"
      />
      <template v-if="$v.confirm.$error" class="user__error">
        <p v-if="!$v.confirm.sameAsPassword" class="user__error">
          {{ $t("users.errors.confirm") }}
        </p>
      </template>
    </div>
  </hcc-modal>
</template>

<script>
import {
  required,
  email,
  minLength,
  sameAs,
} from 'vuelidate/lib/validators';
import { mapState } from 'vuex';
import EventBus from '@/eventBus';
import { validPassword } from '@/utils/helper';
import { NEW_USER } from '@/eventTypes';

export default {
  props: {
    user: {
      required: true,
      type: Object,
    },
    edit: {
      required: true,
      type: Boolean,
    },
  },
  components: {
    HccModal: () => import('@/components/shared/HccModal/index.vue'),
    HccInput: () => import('@/components/shared/HccInput/index.vue'),
  },
  data() {
    return {
      name: '',
      email: '',
      password: null,
      confirm: null,
    };
  },
  validations: {
    email: {
      required,
      email,
    },
    password: {
      required,
      minLength: minLength(8),
      valid: password => validPassword(password),
    },
    confirm: {
      sameAsPassword: sameAs('password'),
    },
    name: { required },
  },
  watch: {
    user() {
      this.buildUserForm();
    },
  },
  computed: {
    ...mapState({
      roles: state => state.shared.roles,
    }),
    validForm() {
      return this.edit ? this.userEdited : !this.$v.$invalid;
    },
    userEdited() {
      return this.user.name !== this.name || this.user.email !== this.email
      || (this.password && this.confirm
          && this.$v.password.minLength && this.$v.confirm.sameAsPassword);
    },
    role() {
      return this.roles.find(({ name }) => name === 'owner');
    },
  },
  methods: {
    buildUserForm() {
      this.name = this.user.name;
      this.email = this.user.email;
    },
    userAction() {
      let payload = {
        name: this.name,
        email: this.email,
        roles: this.role.id,
      };
      if (this.password) {
        payload = {
          ...payload,
          password: this.password,
        };
      }

      if (this.edit) {
        payload = {
          ...payload,
          id: this.user.id,
        };
      }

      EventBus.$emit(NEW_USER, { payload, role: this.role.name });
    },
    clear() {
      this.$v.$reset();
      this.company = { id: '' };
      this.name = '';
      this.email = '';
      this.password = null;
      this.confirm = null;
      this.$modal.hide('Usuario');
    },
    focusRefs() {
      this.$refs.hccInput.focus();
    },
    submit() {
      if (this.validForm) {
        this.userAction();
        this.$modal.hide('Usuario');
      }
    },
  },
};

</script>

<style scoped lang="scss">
@import "~styles/components/_user.scss";
</style>
